import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { LoadingScreenLayout } from "../layout/LoadingScreenLayout";
import configs from "../../utils/configs";
import { useAuth0 } from "@auth0/auth0-react";
import styles from "./RoomEntryModal.scss";
import configManager from "../../singleton/ConfigManager";
import { Button } from "@fluentui/react-components";
import classNames from "classnames";

export const ExitReason = {
  exited: "exited",
  closed: "closed",
  denied: "denied",
  disconnected: "disconnected",
  left: "left",
  full: "full",
  sceneError: "sceneError",
  connectError: "connectError",
  versionMismatch: "versionMismatch",
  signInRequired: "signInRequired",
  rsvpRequired: "rsvpRequired"
};

const messages = defineMessages({
  [ExitReason.exited]: {
    id: "exited-room-screen.reason.exited",
    defaultMessage: "Your session has ended. Refresh your browser to start a new one."
  },
  [ExitReason.closed]: {
    id: "exited-room-screen.reason.closed",
    defaultMessage: "This Verse is no longer available."
  },
  [ExitReason.denied]: {
    id: "exited-room-screen.reason.denied",
    defaultMessage: "You are not permitted to join this Verse. Please request permission from the Verse creator."
  },
  [ExitReason.disconnected]: {
    id: "exited-room-screen.reason.disconnected",
    defaultMessage: "You have disconnected from the Verse. Refresh the page to try to reconnect."
  },
  [ExitReason.left]: {
    id: "exited-room-screen.reason.left",
    defaultMessage: "You have left the Verse."
  },
  [ExitReason.full]: {
    id: "exited-room-screen.reason.full",
    defaultMessage: "This Verse is full, please try again later."
  },
  [ExitReason.sceneError]: {
    id: "exited-room-screen.reason.scene-error",
    defaultMessage: "The Verse failed to load."
  },
  [ExitReason.connectError]: {
    id: "exited-room-screen.reason.connect-error",
    defaultMessage: "Unable to connect to this Verse, please try again later."
  },
  [ExitReason.versionMismatch]: {
    id: "exited-room-screen.reason.version-mismatch",
    defaultMessage: "The version you deployed is not available yet. Your browser will refresh in 5 seconds."
  },
  [ExitReason.signInRequired]: {
    id: "exited-room-screen.reason.sign-in-required",
    defaultMessage: "You must sign in to join this Verse."
  },
  [ExitReason.rsvpRequired]: {
    id: "exited-room-screen.reason.rsvp-required",
    defaultMessage: "You must RSVP to join this event."
  }
});

export function ExitedRoomScreen({ reason, showTerms, termsUrl, showSourceLink }) {
  const intl = useIntl();

  const { loginWithRedirect, loginWithPopup, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const rsvpUrl = APP.hub?.user_data?.event_slug
    ? configs.CUSTOM_API_HOST + "/events/" + APP.hub.user_data?.event_slug + "/login?redirect=" + window.location.href
    : "";
  const eventData = APP.eventData;

  const login = async () => {
    await loginWithPopup();

    const token = await getAccessTokenSilently({
      audience: configs["AUTH0_AUDIENCE"]
    });

    if (token) {
      localStorage.setItem("auth0Token", token);
    }

    window.location.reload();
  };


  useEffect(() => {
    // Check if user is authenticated and the ExitReason is signInRequired, if so, get the token silently and set it in local storage, then refresh the page
    if (!isLoading && isAuthenticated && reason === ExitReason.signInRequired) {
      getAccessTokenSilently()
        .then(token => {
          localStorage.setItem("auth0Token", token);
          window.location.reload();
        })
        .catch(error => {
          console.error(error);
        });
    }
  });

  let subtitle = null;
  if (reason === ExitReason.closed) {
    const contactEmail = intl.formatMessage({ id: "contact-email" });

    subtitle = (
      <>
        <h2 className={styles.title}>
          <FormattedMessage
            id="exited-room-screen.no-longer-available"
            defaultMessage="Sorry, this Verse is no longer available."
          />
        </h2>
        {showTerms && (
          <p className={configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""}>
            <FormattedMessage
              id="exited-room-screen.closed-room-tos"
              defaultMessage="A Verse may be closed by the owner, or if we receive reports that it violates our <toslink>Terms of Use</toslink>."
              values={{
                // eslint-disable-next-line react/display-name
                toslink: chunks => (
                  <a target="_blank" rel="noreferrer noopener" href={termsUrl}>
                    {chunks}
                  </a>
                )
              }}
            />
          </p>
        )}
        <p className={configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""}>
          <FormattedMessage
            id="exited-room-screen.contact-us"
            defaultMessage="If you have questions, contact us at {contactEmail}."
            values={{ contactEmail: <a href={`mailto:${contactEmail}`}>{contactEmail}</a> }}
          />
        </p>
        {showSourceLink && (
          <p className={configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""}>
            <FormattedMessage
              id="exited-room-screen.source-link"
              defaultMessage="If you'd like to run your own server, {appName}'s source code is available on <a>GitHub</a>."
              values={{
                // eslint-disable-next-line react/display-name
                a: chunks => <a href="https://github.com/mozilla/hubs">{chunks}</a>,
                appName: configs.APP_NAME
              }}
            />
          </p>
        )}

        <Button as="a" appearance="primary" href="/">
          <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Back to Home" />
        </Button>
      </>
    );
  } else {
    const tcpUrl = new URL(document.location.toString());
    const tcpParams = new URLSearchParams(tcpUrl.search);
    tcpParams.set("force_tcp", true);
    tcpUrl.search = tcpParams.toString();

    subtitle = (
      <>
        {reason === ExitReason.rsvpRequired && eventData && (
          <div className={styles.center}>
            <h2 className={styles.title}>{eventData.title || eventData.name}</h2>
            {eventData.description && (
              <>
                <p className={styles.roomDescription}>{eventData.description}</p>
              </>
            )}
          </div>
        )}

        <h2
          className={classNames(
            styles.title,
            configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""
          )}
        >
          {intl.formatMessage(messages[reason])}
        </h2>

        {reason === ExitReason.connectError && (
          <p className={configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""}>
            <FormattedMessage
              id="exited-room-screen.connect-tcp"
              defaultMessage="You can try <a>connecting via TCP</a>, which may work better on some networks."
              values={{
                // eslint-disable-next-line react/display-name
                a: chunks => <a href={tcpUrl.toString()}>{chunks}</a>
              }}
            />
          </p>
        )}
        {![
          ExitReason.left,
          ExitReason.disconnected,
          ExitReason.sceneError,
          ExitReason.signInRequired,
          ExitReason.rsvpRequired
        ].includes(reason) && (
          <p className={configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""}>
            <FormattedMessage
              id="exited-room-screen.create-room"
              defaultMessage="You can also <a>create a new Verse</a>."
              values={{
                // eslint-disable-next-line react/display-name
                a: chunks => <a href="/">{chunks}</a>
              }}
            />
          </p>
        )}

        {/* If ExitReason === signInRequired show a signIn button */}
        {reason === ExitReason.signInRequired && (
          <Button appearance="primary" onClick={loginWithPopup}>
            <FormattedMessage id="exited-room-screen.sign-in-button" defaultMessage="Sign In" />
          </Button>
        )}

        {reason === ExitReason.rsvpRequired && (
          <>
            <Button as="a" appearance="primary" href={rsvpUrl}>
              <FormattedMessage id="exited-room-screen.rsvp-button" defaultMessage="RSVP" />
            </Button>
            <p></p>
            <p>
              <FormattedMessage
                id="exited-room-screen.rsvp-hint"
                defaultMessage="Already RSVP’d? Just sign in to join."
              />
              &nbsp;
              <a href="javascript:void(0)" onClick={login}>
                <FormattedMessage id="exited-room-screen.sign-in-button" defaultMessage="Sign In" />
              </a>
            </p>
          </>
        )}

        {reason === ExitReason.denied && (
          <Button as="a" appearance="primary" href="/">
            <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Back to Home" />
          </Button>
        )}

        {reason !== ExitReason.signInRequired && reason !== ExitReason.rsvpRequired && reason !== ExitReason.denied && (
          <Button as="a" appearance="primary" href={window.location.href}>
            <FormattedMessage id="exited-room-screen.refresh-page-button" defaultMessage="Return to Verse" />
          </Button>
        )}
      </>
    );
  }

  return <LoadingScreenLayout center={subtitle} showLogo={false} />;
}

ExitedRoomScreen.propTypes = {
  reason: PropTypes.string.isRequired,
  showTerms: PropTypes.bool,
  termsUrl: PropTypes.string,
  showSourceLink: PropTypes.bool
};
